import { makeAutoObservable } from 'mobx'
import { apiClient } from 'api/trpc/trpc-client'
import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils'
import React from 'react'
import { MainContainerStore } from '../MainContainerStore'
import { notificationsCache } from 'cache/notificationsCache'
import WebSocketStore from 'websockets/WebSocketStore'
import { snackbarInfo } from 'reusable/mui/snackbar/snackbar'
import ObservableRef from 'reusable/mobx-store-utils/ObservableRef'
import { WebSocketResponseDto } from '@gap/backend/websockets/models/WebSocketResponseDto'
import { getFullNameFromUser } from '@gap/utils/utils'
import ModalState from 'reusable/mobx-helpers/ModalState'
import RouterStore from '../../RouterStore'

interface NotificationsStoreParams {
   routerStore: RouterStore
   mainContainerStore: MainContainerStore
   webSocketStore: WebSocketStore
}
export class NotificationsStore {
   sp
   showOnlyUnread = notificationsCache.showOnlyUnread.get() || false
   notificationToggleRef = new ObservableRef<HTMLButtonElement>()
   notificationModalState = new ModalState({
      onClose: () => {
         this.notificationDialogComponent = undefined
      }
   })
   notificationDialogComponent: React.FC | undefined = undefined

   constructor(sp: NotificationsStoreParams) {
      makeAutoObservable(this)
      this.sp = sp
   }

   get unreadNotificationCount() {
      return this.filteredNotifications.filter((n) => !n.read).length
   }

   get filteredNotifications() {
      return this.showOnlyUnread
         ? this.allNotifications.filter(({ read }) => !read)
         : this.allNotifications
   }

   get orgGuardStore() {
      return this.sp.mainContainerStore.sp.orgGuardStore
   }

   get allNotifications() {
      return this.orgGuardStore.userData.notifications || []
   }

   openNotificationDialog = (Component: React.FC) => {
      this.notificationDialogComponent = () => <Component />
      this.notificationModalState.open()
   }

   markAllAsRead = async (_event: React.MouseEvent<HTMLElement>) => {
      const unreadNotifications = this.filteredNotifications.filter(n => !n.read)
      unreadNotifications.forEach((n) => (n.read = true))
      await apiClient.notifications.markAllAsRead.mutate({ ids: unreadNotifications.map(n => n.id) })
   }

   toggleShowUnread = (_event: React.SyntheticEvent, value: number) => {
      this.showOnlyUnread = !!value
      notificationsCache.showOnlyUnread.set(this.showOnlyUnread)
   }

   subscribeToNotificationEvents = async () => {
      const user = this.orgGuardStore.userData
      await this.sp.webSocketStore.addSubscriptions(
         {
            orgId: user.userOrg.orgId,
            userId: user.id,
            subscriptions: [
               {
                  event: 'notification',
                  entityId: user.id, // make the entityId the userId
                  message: `User ${getFullNameFromUser(user)} subscribed to the notification event`,
                  callback: this.reactToNotificationReceived
               }
            ]
         }
      )
   }

   reactToNotificationReceived = async (response: WebSocketResponseDto) => {
      const notification = await apiClient.notifications.get.query({ id: response.entityId })
      if (notification) {
         this.allNotifications.push(notification)
         snackbarInfo(notification.title)
      }
   }
}

export const { Provider: NotificationsStoreProvider, useProvidedStore: useNotificationsStore } =
   createStoreContext(NotificationsStore)
